import { useMemo } from 'react'

const MEMBER_COLORS = {
  slate: '#64748b', rose: '#f43f5e', amber: '#f59e0b', emerald: '#10b981',
  sky: '#0ea5e9', violet: '#8b5cf6', pink: '#ec4899', orange: '#f97316',
  teal: '#14b8a6', indigo: '#6366f1',
}

function fmt(isoStr) {
  return new Date(isoStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
}

function dayLabel(dateStr) {
  const d = new Date(dateStr + 'T12:00:00')
  const today = new Date()
  const tomorrow = new Date(today.getTime() + 86400000)
  if (dateStr === today.toISOString().split('T')[0]) return 'Today'
  if (dateStr === tomorrow.toISOString().split('T')[0]) return 'Tomorrow'
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
}

export function UpcomingEventsPanel({ data }) {
  const { events = [], members = [] } = data

  const memberMap = useMemo(() =>
    Object.fromEntries(members.map((m) => [m.id, m])),
    [members]
  )

  const today = new Date().toISOString().split('T')[0]
  const upcoming = useMemo(() => {
    const sorted = [...events]
      .filter((e) => e.start_time >= today + 'T00:00:00')
      .sort((a, b) => a.start_time.localeCompare(b.start_time))
      .slice(0, 8)

    const grouped = []
    let lastDate = null
    for (const e of sorted) {
      const d = e.start_time.split('T')[0]
      if (d !== lastDate) {
        grouped.push({ date: d, events: [] })
        lastDate = d
      }
      grouped[grouped.length - 1].events.push(e)
    }
    return grouped
  }, [events, today])

  if (upcoming.length === 0) {
    return (
      <div className="flex flex-col h-full items-center justify-center panel-animate-in">
        <div className="text-5xl sm:text-6xl mb-3">📅</div>
        <p className="text-gray-400 text-lg sm:text-xl">Nothing coming up</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full px-4 sm:px-8 py-4 sm:py-6 panel-animate-in min-h-0">
      <p className="text-gray-500 text-xs sm:text-sm font-semibold uppercase tracking-widest mb-2">Coming Up</p>

      <div className="flex-1 space-y-4 overflow-y-auto min-h-0 scrollbar-hide">
        {upcoming.map(({ date, events: dayEvents }) => (
          <div key={date}>
            <h3 className="text-gray-400 text-sm sm:text-base font-semibold mb-2">{dayLabel(date)}</h3>
            <div className="space-y-1.5">
              {dayEvents.map((event) => {
                const eventMembers = (event.event_members ?? []).map((em) => memberMap[em.user_id]).filter(Boolean)
                return (
                  <div key={event.id} className="flex items-center gap-3 bg-white/8 rounded-xl px-3 sm:px-4 py-2.5">
                    <span className="text-gray-400 text-xs sm:text-sm w-16 sm:w-20 flex-shrink-0">
                      {event.is_all_day ? 'All day' : fmt(event.start_time)}
                    </span>
                    <span className="text-white text-sm sm:text-lg font-medium flex-1 truncate">{event.title}</span>
                    <div className="flex gap-1 flex-shrink-0">
                      {eventMembers.slice(0, 3).map((m) => (
                        <div
                          key={m.id}
                          className="w-6 h-6 sm:w-7 sm:h-7 rounded-full text-white text-xs font-bold flex items-center justify-center"
                          style={{ backgroundColor: MEMBER_COLORS[m.memberColor] ?? '#8b5cf6' }}
                        >
                          {m.displayName?.[0]?.toUpperCase()}
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
